/**
 * Accessibility utilities for enhanced user experience
 */

// Focus management utilities
export class FocusManager {
  private static focusStack: HTMLElement[] = [];

  /**
   * Trap focus within a container element
   */
  static trapFocus(container: HTMLElement): () => void {
    const focusableElements = this.getFocusableElements(container);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.restoreFocus();
      }
    };

    // Store current focus
    this.storeFocus();

    // Focus first element
    firstElement?.focus();

    // Add event listeners
    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('keydown', handleEscapeKey);

    // Return cleanup function
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }

  /**
   * Get all focusable elements within a container
   */
  static getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors)) as HTMLElement[];
  }

  /**
   * Store current focus for later restoration
   */
  static storeFocus(): void {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement) {
      this.focusStack.push(activeElement);
    }
  }

  /**
   * Restore previously stored focus
   */
  static restoreFocus(): void {
    const element = this.focusStack.pop();
    if (element) {
      element.focus();
    }
  }

  /**
   * Move focus to element with announcement
   */
  static moveFocusTo(element: HTMLElement, announce = true): void {
    element.focus();
    if (announce) {
      this.announceToScreenReader(`Focus moved to ${element.getAttribute('aria-label') || element.textContent || 'element'}`);
    }
  }

  /**
   * Announce message to screen readers
   */
  static announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
}

// Keyboard navigation utilities
export class KeyboardNavigation {
  /**
   * Handle arrow key navigation for a group of elements
   */
  static handleArrowNavigation(
    elements: HTMLElement[],
    currentIndex: number,
    key: string,
    orientation: 'horizontal' | 'vertical' | 'both' = 'both'
  ): number {
    let newIndex = currentIndex;

    switch (key) {
      case 'ArrowRight':
        if (orientation === 'horizontal' || orientation === 'both') {
          newIndex = (currentIndex + 1) % elements.length;
        }
        break;
      case 'ArrowLeft':
        if (orientation === 'horizontal' || orientation === 'both') {
          newIndex = currentIndex === 0 ? elements.length - 1 : currentIndex - 1;
        }
        break;
      case 'ArrowDown':
        if (orientation === 'vertical' || orientation === 'both') {
          newIndex = (currentIndex + 1) % elements.length;
        }
        break;
      case 'ArrowUp':
        if (orientation === 'vertical' || orientation === 'both') {
          newIndex = currentIndex === 0 ? elements.length - 1 : currentIndex - 1;
        }
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = elements.length - 1;
        break;
    }

    if (newIndex !== currentIndex) {
      elements[newIndex]?.focus();
    }

    return newIndex;
  }

  /**
   * Create keyboard event handler for element activation
   */
  static createActivationHandler(callback: () => void) {
    return (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        callback();
      }
    };
  }
}

// Screen reader utilities
export class ScreenReaderUtils {
  /**
   * Create accessible description for complex UI elements
   */
  static createDescription(element: HTMLElement, description: string): string {
    const descId = `desc-${Math.random().toString(36).substr(2, 9)}`;
    
    const descElement = document.createElement('div');
    descElement.id = descId;
    descElement.className = 'sr-only';
    descElement.textContent = description;
    
    document.body.appendChild(descElement);
    element.setAttribute('aria-describedby', descId);
    
    return descId;
  }

  /**
   * Update live region with new content
   */
  static updateLiveRegion(regionId: string, content: string, priority: 'polite' | 'assertive' = 'polite'): void {
    let region = document.getElementById(regionId);
    
    if (!region) {
      region = document.createElement('div');
      region.id = regionId;
      region.setAttribute('aria-live', priority);
      region.setAttribute('aria-atomic', 'true');
      region.className = 'sr-only';
      document.body.appendChild(region);
    }
    
    region.textContent = content;
  }

  /**
   * Create skip link for keyboard navigation
   */
  static createSkipLink(targetId: string, text: string): HTMLElement {
    const skipLink = document.createElement('a');
    skipLink.href = `#${targetId}`;
    skipLink.className = 'skip-link';
    skipLink.textContent = text;
    
    skipLink.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.getElementById(targetId);
      if (target) {
        target.focus();
        target.scrollIntoView({ behavior: 'smooth' });
      }
    });
    
    return skipLink;
  }
}

// Color contrast utilities
export class ColorContrastUtils {
  /**
   * Calculate relative luminance of a color
   */
  static getRelativeLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  /**
   * Calculate contrast ratio between two colors
   */
  static getContrastRatio(color1: [number, number, number], color2: [number, number, number]): number {
    const l1 = this.getRelativeLuminance(...color1);
    const l2 = this.getRelativeLuminance(...color2);
    
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    
    return (lighter + 0.05) / (darker + 0.05);
  }

  /**
   * Check if color combination meets WCAG contrast requirements
   */
  static meetsContrastRequirement(
    foreground: [number, number, number],
    background: [number, number, number],
    level: 'AA' | 'AAA' = 'AA',
    size: 'normal' | 'large' = 'normal'
  ): boolean {
    const ratio = this.getContrastRatio(foreground, background);
    
    if (level === 'AAA') {
      return size === 'large' ? ratio >= 4.5 : ratio >= 7;
    } else {
      return size === 'large' ? ratio >= 3 : ratio >= 4.5;
    }
  }
}

// Motion preferences utilities
export class MotionUtils {
  /**
   * Check if user prefers reduced motion
   */
  static prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Get animation duration based on user preferences
   */
  static getAnimationDuration(normalDuration: number): number {
    return this.prefersReducedMotion() ? Math.min(normalDuration * 0.1, 100) : normalDuration;
  }

  /**
   * Create motion-safe animation config
   */
  static createMotionSafeConfig(config: any): any {
    if (this.prefersReducedMotion()) {
      return {
        ...config,
        duration: this.getAnimationDuration(config.duration || 300),
        ease: 'linear',
        scale: 1,
        x: 0,
        y: 0,
        rotate: 0
      };
    }
    return config;
  }
}

// Touch and gesture utilities for mobile accessibility
export class TouchUtils {
  /**
   * Check if device supports touch
   */
  static isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Ensure minimum touch target size (44px recommended)
   */
  static ensureMinimumTouchTarget(element: HTMLElement, minSize = 44): void {
    const rect = element.getBoundingClientRect();
    
    if (rect.width < minSize || rect.height < minSize) {
      element.style.minWidth = `${minSize}px`;
      element.style.minHeight = `${minSize}px`;
      element.style.display = 'inline-flex';
      element.style.alignItems = 'center';
      element.style.justifyContent = 'center';
    }
  }

  /**
   * Add touch feedback for better user experience
   */
  static addTouchFeedback(element: HTMLElement): void {
    if (!this.isTouchDevice()) return;

    element.addEventListener('touchstart', () => {
      element.style.opacity = '0.7';
    }, { passive: true });

    element.addEventListener('touchend', () => {
      setTimeout(() => {
        element.style.opacity = '';
      }, 150);
    }, { passive: true });

    element.addEventListener('touchcancel', () => {
      element.style.opacity = '';
    }, { passive: true });
  }
}

// Utility to create accessible components
export const createAccessibleComponent = {
  /**
   * Add comprehensive accessibility attributes to an element
   */
  enhance(element: HTMLElement, options: {
    role?: string;
    label?: string;
    description?: string;
    expanded?: boolean;
    controls?: string;
    live?: 'polite' | 'assertive' | 'off';
  }): void {
    const { role, label, description, expanded, controls, live } = options;

    if (role) element.setAttribute('role', role);
    if (label) element.setAttribute('aria-label', label);
    if (description) ScreenReaderUtils.createDescription(element, description);
    if (expanded !== undefined) element.setAttribute('aria-expanded', expanded.toString());
    if (controls) element.setAttribute('aria-controls', controls);
    if (live) element.setAttribute('aria-live', live);

    // Ensure keyboard accessibility
    if (!element.hasAttribute('tabindex') && (role === 'button' || role === 'link')) {
      element.setAttribute('tabindex', '0');
    }

    // Add touch feedback for mobile
    TouchUtils.addTouchFeedback(element);
    TouchUtils.ensureMinimumTouchTarget(element);
  }
};